import React, { useState } from "react";
import {
  Card,
  Box,
  Typography,
  Stack,
  CircularProgress,
  Button,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useQuery } from "@apollo/client";
import { GET_TASKS } from "../../../apis/task-apis/taskApis";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ReadinessChip from "../../../components/ReadinessChip/ReadinessChip";
import DomainChip from "../../../components/DomainChip/DomainChip";
import { useNavigate } from "react-router-dom";

const OpenTasksCard = ({ sx = {} }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [viewAllHover, setViewAllHover] = useState(false);

  // Fetch tasks from API
  const { data, loading, error } = useQuery(GET_TASKS, {
    variables: {
      filter: {
        status_key: "open",
      },
      page: 1,
      limit: 100,
    },
  });

  const tasks = data?.getTasks?.data || [];
  const hasTasks = tasks && tasks.length > 0;

  // TaskItem component (matching ControlItem in ControlSummary exactly)
  const TaskItem = ({ task, index }) => {
    const [isHovered, setIsHovered] = useState(false);

    const handleTaskClick = () => {
      navigate(`/admin/tasks?taskId=${task.id}`);
    };
    return (
      <Box
        key={index}
        onClick={handleTaskClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          p: 2.5,
          border: `1px solid ${theme.customColors.border.subtle}`,
          borderRadius: "12px",
          cursor: "pointer",
          backgroundColor: isHovered
            ? theme.customColors.background.hover
            : "#FFFFFF",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          transform: isHovered ? "translateY(-2px)" : "translateY(0)",
          boxShadow: isHovered ? "0 4px 12px -2px rgba(0, 0, 0, 0.08)" : "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ flex: 1, pr: 2, minWidth: 0 }}>
            {/* Title */}
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "15px",
                color: theme.customColors.text.strong,
                mb: 0.5,
                lineHeight: 1.4,
                letterSpacing: "-0.02em",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                hyphens: "auto",
              }}
            >
              {task.title}
            </Typography>

            {/* Domain badges */}
            {task.domains && task.domains.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  mt: 3,
                }}
              >
                {task.domains.map((domain, idx) => (
                  <DomainChip
                    key={idx}
                    domainKey={domain.key}
                    domainTitle={domain.title}
                  />
                ))}
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ArrowForwardIcon
              sx={{
                fontSize: "18px",
                color: theme.customColors.text.disabled,
                opacity: isHovered ? 1 : 0,
                transform: isHovered ? "translateX(0)" : "translateX(-4px)",
                transition: "all 0.2s ease",
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Card
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        border: `1px solid ${theme.customColors.border.main}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        transition: "box-shadow 0.3s ease",
        "&:hover": {
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        },
        ...sx,
      }}
    >
      {/* Header - Matching ControlSummary */}
      <Box
        sx={{
          p: "24px 24px 20px",
          borderBottom: `1px solid ${theme.customColors.background.tableRow}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "18px",
                color: theme.customColors.text.strong,
                letterSpacing: "-0.02em",
              }}
            >
              Open Tasks
            </Typography>
          </Box>

          <Button
            component="button"
            onClick={() => navigate("/admin/tasks")}
            onMouseEnter={() => setViewAllHover(true)}
            onMouseLeave={() => setViewAllHover(false)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "4px 12px",
              minWidth: 0,
              textTransform: "none",
              backgroundColor: viewAllHover
                ? "rgba(58, 61, 238, 0.08)"
                : "transparent",
              border: "none",
              borderRadius: "8px",
              color: "#6366f1",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "background-color 0.2s",
              "&:hover": {
                backgroundColor: "rgba(58, 61, 238, 0.08)",
              },
            }}
          >
            View All
            <ArrowForwardIcon sx={{ fontSize: "14px" }} />
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          p: "20px 24px 24px",
          overflowY: "auto",
          minHeight: 0,
          // Custom scrollbar styling
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(theme.customColors.border.main, 0.7),
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: alpha(theme.customColors.border.main, 0.7),
            },
          },
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 250,
            }}
          >
            <CircularProgress
              size={44}
              thickness={4}
              sx={{ color: theme.palette.primary.main }}
            />
            <Typography
              sx={{
                mt: 2.5,
                color: theme.customColors.text.secondary,
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              Loading tasks...
            </Typography>
          </Box>
        ) : hasTasks ? (
          <Stack spacing={2}>
            {tasks.map((task, index) => (
              <TaskItem key={task.id || index} task={task} index={index} />
            ))}
          </Stack>
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              px: 4,
              backgroundColor: theme.customColors.background.hover,
              borderRadius: "12px",
              border: `1px dashed ${theme.customColors.border.subtle}`,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: theme.customColors.text.primary,
                fontWeight: 600,
                mb: 1,
              }}
            >
              All caught up
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.customColors.text.secondary,
                fontSize: "13px",
              }}
            >
              No open tasks at the moment. New tasks will appear here when they
              become available.
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default OpenTasksCard;
